import React, { useEffect, useState } from 'react';
import { Camera, MapPin } from 'lucide-react';
import { supabase } from '../../../../src/lib/supabase';
import { TIPO_VISITA_LABELS } from '../../Rotas/CalendarioMensal';

interface VisitaRegistro {
    id: string;
    status: string;
    checkin_em?: string | null;
    checkout_em?: string | null;
    visitado_em?: string | null;
    descricao_visita?: string | null;
    foto_url?: string | null;
    rotas_comerciais?: { data: string; vendedor_id: string } | null;
    tarefas?: { tipo?: string | null; objetivo?: string | null } | null;
}

interface HistoricoVisitasProps {
    negotiationId: number;
}

const hora = (iso?: string | null) =>
    iso ? new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : null;

/** Histórico de visitas comerciais do lead (Rotas v2): check-in/out, foto do medidor e resultado. */
const HistoricoVisitas: React.FC<HistoricoVisitasProps> = ({ negotiationId }) => {
    const [visitas, setVisitas] = useState<VisitaRegistro[]>([]);
    const [nomes, setNomes] = useState<Record<string, string>>({});

    useEffect(() => {
        const fetchVisitas = async () => {
            const { data, error } = await supabase
                .from('rota_paradas')
                .select('id, status, checkin_em, checkout_em, visitado_em, descricao_visita, foto_url, rotas_comerciais(data, vendedor_id), tarefas(tipo, objetivo)')
                .eq('negociacao_id', negotiationId)
                .in('status', ['visitado', 'em_visita'])
                .order('visitado_em', { ascending: false });
            if (error) { console.error('Erro ao buscar visitas:', error); return; }
            const lista = (data || []) as unknown as VisitaRegistro[];
            setVisitas(lista);

            const ids = Array.from(new Set(lista.map(v => v.rotas_comerciais?.vendedor_id).filter(Boolean))) as string[];
            if (ids.length > 0) {
                const { data: profs } = await supabase.from('profiles').select('id, full_name').in('id', ids);
                const map: Record<string, string> = {};
                (profs || []).forEach((p: any) => { map[p.id] = p.full_name; });
                setNomes(map);
            }
        };
        fetchVisitas();
    }, [negotiationId]);

    if (visitas.length === 0) return null;

    return (
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-3 text-gray-400 dark:text-slate-500">
                <MapPin size={16} />
                <span className="text-xs font-bold uppercase tracking-wide">Histórico de Visitas ({visitas.length})</span>
            </div>
            <div className="space-y-3">
                {visitas.map(v => {
                    const dataVisita = v.checkin_em || v.visitado_em || v.rotas_comerciais?.data;
                    const tipo = v.tarefas?.tipo ? TIPO_VISITA_LABELS[v.tarefas.tipo] : null;
                    return (
                        <div key={v.id} className="p-3 rounded-lg bg-gray-50 dark:bg-slate-800/50">
                            <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <p className="text-xs font-bold text-gray-800 dark:text-slate-100">
                                        {dataVisita ? new Date(dataVisita).toLocaleDateString('pt-BR') : '—'}
                                    </p>
                                    {tipo && (
                                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">{tipo}</span>
                                    )}
                                    {v.status === 'em_visita' && (
                                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">Em andamento</span>
                                    )}
                                </div>
                                <span className="text-[10px] text-gray-400">
                                    {hora(v.checkin_em) || '—'}{v.checkout_em ? ` → ${hora(v.checkout_em)}` : ''}
                                </span>
                            </div>
                            <p className="text-[11px] text-gray-500 dark:text-slate-400 mb-1">
                                Responsável: <span className="font-semibold text-gray-700 dark:text-slate-200">{nomes[v.rotas_comerciais?.vendedor_id || ''] || '—'}</span>
                            </p>
                            {v.tarefas?.objetivo && (
                                <p className="text-[11px] text-gray-600 dark:text-slate-300 mb-1">🎯 {v.tarefas.objetivo}</p>
                            )}
                            {v.descricao_visita && (
                                <p className="text-xs text-gray-700 dark:text-slate-200 leading-relaxed mb-1.5">{v.descricao_visita}</p>
                            )}
                            {v.foto_url && (
                                <a
                                    href={v.foto_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-primary hover:underline"
                                >
                                    <Camera size={12} /> Foto do medidor
                                </a>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default HistoricoVisitas;
